import type { Lang } from '@ahd/shared/types/ahd-fleet-api/lang';
import type { TFunction } from 'src/hooks/use-locales/types';

import isUndefinedOrNull from '@ahd/shared/tools/is-undefined-or-null';
import outputConsole from '@ahd/shared/tools/output-console';
import { FrontendEnvironment } from '@ahd/shared/types/misc/environments';
import { AxiosError, CanceledError } from 'axios';
import { get } from 'es-toolkit/compat';

import modules from '../../modules/_definition/map';
import formatText from '../../utils/texte/formatText';
import { ENVIRONMENT_FRONTEND } from '../../vars';
import { createLog } from '../axios/createLog';
import { ErrorAction, Errors, type FunctionOutputTypeError, type FunctionOutputTypeErrorReturn } from '../types/function-output-type';
import { DBLogLevel, type DBLogSource } from '../types/log';
import { CarDetailsError, type CarDetailsErrorCauses } from './car-details';
import { CarListError, type CarListErrorCauses } from './car-list';
import { ConfigError, type ConfigErrorCauses } from './config';
import { ModuleDisabledError } from './module-disabled';

import type { DebugLevel } from '../../_configs/types';
import type { TranslationsErrors } from '../../locales/errors/translations';

async function handleSingleError(
    translate: TFunction,
    fallbackLanguage: Lang,
    functionName: string,
    error: Error,
    idGroup: number,
    origin: DBLogSource,
    debug: DebugLevel,
    noLog?: boolean
): Promise<FunctionOutputTypeError> {
    const languageFile = isUndefinedOrNull(translate)
        ? ((await import(/* webpackChunkName: "localeErrorTranslations" */ `../../locales/errors/${fallbackLanguage}`))?.default as TranslationsErrors)
        : undefined;

    let newErrorElement: FunctionOutputTypeError = {
        action: ErrorAction.RELOAD,
        description: translate?.(`errors:default.description`) || get(languageFile, `default.description`),
        function: functionName,
        heading: translate?.(`errors:default.heading`) || get(languageFile, `default.heading`),
        message: error?.message,
        name: `UnhandledError:${error?.name}`,
        stack: ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER ? error?.stack : undefined,
        status: 500,
    };

    if (error instanceof AxiosError || error instanceof CanceledError) {
        newErrorElement = {
            ...newErrorElement,
            api: error?.config?.url,
            errorObject: error?.response?.data,
            message: error?.response?.data?.message || `${error?.message} - ${error?.code}`,
            name: Errors.API,
            status: error?.status || 500,
        };
    } else if (error instanceof CarDetailsError) {
        const translateErrorKey: `${Errors.CAR_DETAILS}.${CarDetailsErrorCauses}` = `${Errors.CAR_DETAILS}.${error?.cause}`;
        newErrorElement = {
            ...newErrorElement,
            cause: error?.cause,
            description: translate?.(`errors:${translateErrorKey}.description`) || get(languageFile, `${translateErrorKey}.description`),
            heading: translate?.(`errors:${translateErrorKey}.heading`) || get(languageFile, `${translateErrorKey}.heading`),
            name: Errors.CAR_DETAILS,
        };
    } else if (error instanceof CarListError) {
        const translateErrorKey: `${Errors.CAR_LIST}.${CarListErrorCauses}` = `${Errors.CAR_LIST}.${error?.cause}`;
        newErrorElement = {
            ...newErrorElement,
            cause: error?.cause,
            description: translate?.(`errors:${translateErrorKey}.description`) || get(languageFile, `${translateErrorKey}.description`),
            heading: translate?.(`errors:${translateErrorKey}.heading`) || get(languageFile, `${translateErrorKey}.heading`),
            name: Errors.CAR_LIST,
        };
    } else if (error instanceof ConfigError) {
        const translateErrorKey: `${Errors.CONFIG}.${ConfigErrorCauses}` = `${Errors.CONFIG}.${error?.cause}`;
        newErrorElement = {
            ...newErrorElement,
            cause: error?.cause,
            description: translate?.(`errors:${translateErrorKey}.description`) || get(languageFile, `${translateErrorKey}.description`),
            heading: translate?.(`errors:${translateErrorKey}.heading`) || get(languageFile, `${translateErrorKey}.heading`),
            name: Errors.CONFIG,
        };
    } else if (error instanceof ModuleDisabledError) {
        newErrorElement = {
            ...newErrorElement,
            action: ErrorAction.NONE,
            description: formatText(
                translate?.(`errors:${Errors.MODULE_DISABLED}.description`) || get(languageFile, `${Errors.MODULE_DISABLED}.description`),
                modules?.find((m) => m?.i === error?.module)?.title
            ),
            heading: formatText(translate?.(`errors:${Errors.MODULE_DISABLED}.heading`) || get(languageFile, `${Errors.MODULE_DISABLED}.heading`), modules?.find((m) => m?.i === error?.module)?.title),
            name: Errors.MODULE_DISABLED,
        };
    } else if (error instanceof RangeError || error instanceof TypeError || error instanceof Error) {
        newErrorElement = {
            ...newErrorElement,
            errorObject: error,
            name: Errors.ERROR,
        };
    } else {
        newErrorElement = {
            ...newErrorElement,
            errorObject: error,
            name: Errors.ERROR,
        };
    }
    outputConsole('error', 'Server', functionName, newErrorElement?.message, newErrorElement);

    if (!noLog) {
        await createLog(
            {
                idGroup,
                idProject: undefined,
                level: DBLogLevel.Error,
                message: newErrorElement?.message,
                origin,
                raw: newErrorElement,
                title: newErrorElement?.name,
            },
            debug
        );
    }

    return newErrorElement;
}

export async function errorHandler(
    translate: TFunction,
    fallbackLanguage: Lang,
    functionName: string,
    errors: unknown,
    idGroup: number,
    origin: DBLogSource,
    debug: DebugLevel,
    noLog?: boolean
): Promise<FunctionOutputTypeErrorReturn> {
    const rawErrors = Array.isArray(errors) ? errors?.[0] : errors;
    const newErrors: FunctionOutputTypeErrorReturn['output'] = await handleSingleError(translate, fallbackLanguage, functionName, rawErrors, idGroup, origin, debug, noLog);

    return {
        status: newErrors?.status,
        output: newErrors,
    };
}
